import React, { useState, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

import { CALL_METHODS } from '../../../../types';

import { CallActionEditorProps } from './types';
import { METHOD_DESCRIPTIONS } from './constants';
import { getDefaultPayloadForMethod } from './utils';
import { UIConfigurationEditor } from './components';
import {
  NavigateEditor,
  SetFormFieldsEditor,
  SetContextEditor,
  SearchNonprofitsEditor,
  BatchGenerateEditor,
  LoadNonprofitsEditor,
  LoadCausesEditor,
  LoadPicturesEditor,
  LoadUserEditor,
  SetDefaultPictureEditor,
  DebugEditor,
  DelayEditor,
  ParseDataEditor,
  ReduxEditor,
} from './methodEditors';
import { WaitForContextEditor } from './methodEditors/WaitForContextEditor';

const CallActionEditor: React.FC<CallActionEditorProps> = ({ action, onChange }) => {
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonError, setJsonError] = useState<string>('');

  const handleMethodChange = useCallback(
    (method: string) => {
      const newAction = {
        ...action,
        data: {
          ...action.data,
          method: method as any,
          payload: getDefaultPayloadForMethod(method),
        },
      };
      onChange(newAction);
    },
    [action, onChange]
  );

  const handlePayloadChange = useCallback(
    (payload: any) => {
      onChange({ ...action, data: { ...action.data, payload } });
    },
    [action, onChange]
  );

  const handleUIChange = useCallback(
    (ui: any) => {
      onChange({ ...action, data: { ...action.data, ui } });
    },
    [action, onChange]
  );

  const handleJsonChange = useCallback(
    (jsonStr: string) => {
      try {
        const parsed = JSON.parse(jsonStr);
        setJsonError('');
        onChange({ ...action, data: { ...action.data, payload: parsed } });
      } catch (error) {
        setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      }
    },
    [action, onChange]
  );

  const renderMethodSpecificEditor = () => {
    const { payload, method } = action.data;

    switch (method) {
      case CALL_METHODS.NAVIGATE:
        return <NavigateEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.SET_FORM_FIELDS:
        return <SetFormFieldsEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.SET_CONTEXT:
        return <SetContextEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.SEARCH_NONPROFITS:
        return <SearchNonprofitsEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.BATCH_GENERATE:
        return <BatchGenerateEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.LOAD_NONPROFITS:
        return <LoadNonprofitsEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.LOAD_CAUSES:
        return <LoadCausesEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.LOAD_PICTURES:
        return <LoadPicturesEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.LOAD_USER:
        return <LoadUserEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.SET_DEFAULT_PICTURE:
        return <SetDefaultPictureEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.WAIT_FOR_CONTEXT:
        return <WaitForContextEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.DEBUG:
        return <DebugEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.DELAY:
        return <DelayEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.PARSE_DATA:
        return <ParseDataEditor payload={payload} onChange={handlePayloadChange} />;

      case CALL_METHODS.REDUX:
        return <ReduxEditor payload={payload} onChange={handlePayloadChange} />;

      default:
        return <Alert severity="info">Select a method to configure its specific options.</Alert>;
    }
  };

  return (
    <Box>
      {/* Method Selection */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Method</InputLabel>
        <Select
          value={action.data.method || ''}
          onChange={(e) => handleMethodChange(e.target.value)}
          label="Method"
        >
          {Object.values(CALL_METHODS).map((method) => (
            <MenuItem key={method} value={method}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {method}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {METHOD_DESCRIPTIONS[method]}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {action.data.method && (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>{action.data.method}:</strong> {METHOD_DESCRIPTIONS[action.data.method]}
          </Typography>
        </Alert>
      )}

      {/* Method-specific payload editor */}
      {action.data.method && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Payload Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Configure the specific parameters for this method
              </Typography>
              <Button
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => setShowJsonEditor(!showJsonEditor)}
              >
                {showJsonEditor ? 'Form Editor' : 'JSON Editor'}
              </Button>
            </Box>

            {showJsonEditor ? (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={JSON.stringify(action.data.payload, null, 2)}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  label="Payload JSON"
                  error={!!jsonError}
                  helperText={jsonError || 'Edit the payload as JSON'}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            ) : (
              renderMethodSpecificEditor()
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* UI Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">UI Configuration</Typography>
          <Tooltip title="Optional UI feedback during method execution">
            <HelpIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
          </Tooltip>
        </AccordionSummary>
        <AccordionDetails>
          <UIConfigurationEditor ui={action.data.ui} onChange={handleUIChange} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CallActionEditor;
